import React, { useMemo, useState } from 'react';
import { Box, Button, Chip, TextField, Typography } from '@mui/material';

function formatSlotLabel(iso) {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function ymd(d) {
  const x = new Date(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
}

/**
 * Two-step flow: (1) meeting type chip → (2) date range button → then one wire message to fetch slots.
 */
export function CalcomEventTypePicker({ items, onPick }) {
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const weekEnd = new Date(today);
  weekEnd.setDate(weekEnd.getDate() + 7);

  const [selectedEt, setSelectedEt] = useState(null);

  if (!items?.length) return null;

  const fetchSlotsForRange = (et, newRange) => {
    const merged = {
      ...et,
      startDate: newRange.start,
      endDate: newRange.end,
    };
    const wire = `Show my available Cal.com times for "${merged.title}" (eventTypeId ${merged.id}) from ${merged.startDate} to ${merged.endDate}.`;
    const label = merged.title || merged.slug || 'meeting';
    const displayMessage = `Show available times for “${label}” (${merged.startDate} → ${merged.endDate}).`;
    onPick(merged, { wireMessage: wire, displayMessage });
  };

  return (
    <Box sx={{ my: 1, maxWidth: '100%' }}>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
        Step 1 — Meeting type
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
        {items.map((et) => (
          <Chip
            key={et.id}
            label={et.title || et.slug || `Type ${et.id}`}
            onClick={() => setSelectedEt(et)}
            color="primary"
            variant={selectedEt?.id === et.id ? 'filled' : 'outlined'}
            size="small"
          />
        ))}
      </Box>
      {selectedEt && (
        <>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5, mt: 0.5 }}>
            Step 2 — Date range (loads open times)
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => fetchSlotsForRange(selectedEt, { start: ymd(today), end: ymd(today) })}
            >
              Today
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => fetchSlotsForRange(selectedEt, { start: ymd(tomorrow), end: ymd(tomorrow) })}
            >
              Tomorrow
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => fetchSlotsForRange(selectedEt, { start: ymd(today), end: ymd(weekEnd) })}
            >
              Next 7 days
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
}

/**
 * Up to pageSize slot buttons; "More times" reveals next slice from allTimes (max ~40 from server).
 */
export function CalcomSlotsPicker({ payload, onSendMessage }) {
  const pageSize = payload?.pageSize || 10;
  const allTimes = payload?.allTimes || [];
  const [visibleStart, setVisibleStart] = useState(0);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');

  const slice = useMemo(
    () => allTimes.slice(visibleStart, Math.min(visibleStart + pageSize, allTimes.length)),
    [allTimes, visibleStart, pageSize]
  );

  const canMore = visibleStart + pageSize < allTimes.length;

  const handleBook = () => {
    if (!selectedSlot || !name.trim() || !email.trim() || typeof onSendMessage !== 'function') return;
    const parts = [
      'Please create my Cal.com booking.',
      payload.eventTypeId != null ? `eventTypeId ${payload.eventTypeId}` : '',
      payload.eventSlug ? `eventSlug ${payload.eventSlug}` : '',
      `start ${selectedSlot}`,
      `attendeeName ${name.trim()}`,
      `attendeeEmail ${email.trim()}`,
    ].filter(Boolean);
    const wire = parts.join(' ');
    const displayMessage = `Book ${formatSlotLabel(selectedSlot)}`;
    onSendMessage(wire, { displayMessage });
    setSelectedSlot(null);
    setName('');
    setEmail('');
  };

  return (
    <Box sx={{ my: 1, maxWidth: '100%' }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {slice.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No times in this range.
          </Typography>
        )}
        {slice.map((t) => (
          <Button
            key={t}
            size="small"
            variant={selectedSlot === t ? 'contained' : 'outlined'}
            onClick={() => setSelectedSlot(t)}
            sx={{ textTransform: 'none' }}
          >
            {formatSlotLabel(t)}
          </Button>
        ))}
      </Box>
      {canMore && (
        <Button size="small" onClick={() => setVisibleStart((s) => s + pageSize)} sx={{ mt: 1 }}>
          More times
        </Button>
      )}
      {selectedSlot && (
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1, maxWidth: 320 }}>
          <TextField size="small" label="Your name" value={name} onChange={(e) => setName(e.target.value)} fullWidth />
          <TextField
            size="small"
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
          />
          <Button variant="contained" size="small" onClick={handleBook} disabled={!name.trim() || !email.trim()}>
            Book this time
          </Button>
        </Box>
      )}
    </Box>
  );
}
